import { rulesetEvaluationsQuery } from "@common/api/quoting/ruleset-evaluation"
import { InlineEmpty } from "@common/components/shared/inline-empty"
import { UserInitials } from "@common/components/shared/user-initials"
import { useHasPermission } from "@common/hooks/use-permissions"
import { formatDateTime } from "@common/lib/date-utils"
import { cn } from "@common/lib/utils"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { PageContent } from "@/components/page/page-content"
import { personName } from "@/routes/_layout/settings/_panel/-lib/history"
import { PlaybookBar } from "./-components/playbook-bar"
import { type ActivityEntry, activity } from "./-lib/history"
import { usePlaybook } from "./-lib/playbook"

const ITEM = "grid grid-cols-[1.5rem_1fr_auto] items-start gap-3 py-2.5"

const DOT = "mx-auto mt-2 size-1.5 rounded-full bg-muted-foreground/50"

const WHEN = "whitespace-nowrap text-small-medium text-muted-foreground"

export const Route = createFileRoute(
  "/_layout/settings/_panel/quoting/playbooks/$playbookId/activity",
)({
  component: ActivityPage,
})

function ActivityPage(): React.ReactElement {
  const { playbookId } = Route.useParams()
  const { versions } = usePlaybook(playbookId)
  const canReadEvaluations = useHasPermission("quote_ruleset_evaluation:read")
  const { data } = useQuery({
    ...rulesetEvaluationsQuery({ ruleset_id: playbookId }),
    enabled: canReadEvaluations,
  })
  const entries = activity(versions, data?.data ?? [])

  return (
    <div>
      <PlaybookBar playbookId={playbookId} />
      <PageContent>
        {entries.length === 0 ? (
          <InlineEmpty>No activity yet.</InlineEmpty>
        ) : (
          <ol className="divide-y">
            {entries.map((entry) => (
              <ActivityItem key={entry.key} entry={entry} />
            ))}
          </ol>
        )}
      </PageContent>
    </div>
  )
}

function ActivityItem({ entry }: { entry: ActivityEntry }): React.ReactElement {
  return (
    <li className={ITEM}>
      {entry.by ? <UserInitials user={entry.by} /> : <span className={DOT} />}
      <p
        className={cn(
          "text-body-medium",
          entry.automatic && "text-muted-foreground",
        )}
      >
        {entry.by && (
          <span className="text-body-semibold">{personName(entry.by)} </span>
        )}
        {entry.text}
      </p>
      <time className={WHEN} dateTime={entry.at}>
        {formatDateTime(entry.at)}
      </time>
    </li>
  )
}
